import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { getStatusBadge } from '@/lib/email-formatters';
import { Input } from '@/components/ui/input';
import { Search, Loader2 } from 'lucide-react';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    if (q.length < 3) {
      setResults([]);
      setTotal(0);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.searchEmails(q, 10);
      setResults(data.emails);
      setTotal(data.total);
      if (data.total === 1) {
        navigate(`/emails/${encodeURIComponent(data.emails[0].email)}`);
        setQuery('');
        setOpen(false);
      } else {
        setOpen(true);
      }
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleChange = (value: string) => {
    setQuery(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(value), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const handleSelect = (email: string) => {
    navigate(`/emails/${encodeURIComponent(email)}`);
    setQuery('');
    setOpen(false);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative px-4 py-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search email..."
          className="pl-8 h-8 text-sm bg-muted/50"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && (
        <div className="absolute left-4 right-4 top-full mt-1 z-50 rounded-md border bg-popover shadow-lg max-h-64 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No results
            </div>
          ) : (
            <>
              {results.map((r) => (
                <button
                  key={r.email}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                  onClick={() => handleSelect(r.email)}
                >
                  <span className="font-mono truncate flex-1 mr-2">{r.email}</span>
                  {getStatusBadge(r.status)}
                </button>
              ))}
              {total > results.length && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-t text-center">
                  +{total - results.length} more
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
